import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Switch, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton, Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';

import { useAuth } from '../../providers/AuthProvider';
import { selectUserInfos } from '../../store/userDataStore';
import { useSelector } from 'react-redux';
import { tdGroupService, AdminTDGroup } from '../../services/tdGroupService';
import { gitlabAdminService } from '../../services/gitlabAdminService';
import { userAdminService, AdminUser } from '../../services/userAdminService';
import { projectGroupAdminService, AdminProjectGroup } from '../../services/projectGroupAdminService';

// Le nombre de groupes TD et l'effectif de groupes projet par TD changent chaque année (voir
// TDGroupModel) — cette page remplace l'édition manuelle du code / des fixtures pour ça.
const AdminGroupsPage: React.FC = () => {
  const { getValidToken } = useAuth();
  const userInfos = useSelector(selectUserInfos);
  const isAdmin = userInfos?.access === 'admin';

  const [groups, setGroups] = useState<AdminTDGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newCode, setNewCode] = useState('');
  const [creating, setCreating] = useState(false);

  // Ajustement du nombre de groupes projet, par ligne
  const [generateCounts, setGenerateCounts] = useState<Record<number, string>>({});
  const [generateLoadingId, setGenerateLoadingId] = useState<number | null>(null);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);

  // Composition des binômes (arbitrage final) — ouvert par TD group
  const [managingTd, setManagingTd] = useState<AdminTDGroup | null>(null);
  const [projectGroups, setProjectGroups] = useState<AdminProjectGroup[]>([]);
  const [students, setStudents] = useState<AdminUser[]>([]);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [addSelections, setAddSelections] = useState<Record<number, string>>({});
  const [memberActionLoading, setMemberActionLoading] = useState<string | null>(null);

  const loadGroups = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      setGroups(await tdGroupService.listAdmin(token));
    } catch (e: any) {
      setError(e.message || 'Erreur lors du chargement des groupes TD');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdmin) loadGroups();
  }, [isAdmin, loadGroups]);

  // userInfos est rechargé de façon asynchrone après un F5 : on attend avant de juger l'accès
  if (!userInfos) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Accès réservé aux administrateurs.</Alert>
      </Box>
    );
  }

  const handleCreate = async () => {
    if (!newCode.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await tdGroupService.create(newCode.trim(), token);
      setNewCode('');
      await loadGroups();
    } catch (e: any) {
      setError(e.message || "Erreur lors de la création du groupe TD");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (group: AdminTDGroup) => {
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await tdGroupService.setActive(group.id, !group.active, token);
      await loadGroups();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleGenerate = async (group: AdminTDGroup) => {
    const count = parseInt(generateCounts[group.id] || '', 10);
    if (!count || count < 1) return;
    setGenerateLoadingId(group.id);
    setGenerateMessage(null);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const result = await gitlabAdminService.generateProjectGroups(group.code, count, token);
      setGenerateMessage(`${group.code} : ${result.message}`);
      setGenerateCounts({ ...generateCounts, [group.id]: '' });
      await loadGroups();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la génération des groupes projet');
    } finally {
      setGenerateLoadingId(null);
    }
  };

  const handleGenerateSandboxes = async () => {
    setSandboxLoading(true);
    setGenerateMessage(null);
    setError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const result = await gitlabAdminService.generateSandboxGroups(token);
      setGenerateMessage(result.message);
      await loadGroups();
    } catch (e: any) {
      setError(e.message || 'Erreur lors de la création des bacs à sable');
    } finally {
      setSandboxLoading(false);
    }
  };

  const openManage = async (group: AdminTDGroup) => {
    setManagingTd(group);
    setMembershipError(null);
    setMembershipLoading(true);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      const [allGroups, allStudents] = await Promise.all([
        projectGroupAdminService.list(token),
        userAdminService.listUsers(token),
      ]);
      setProjectGroups(allGroups.filter((g) => g.td_group === group.code));
      setStudents(allStudents.filter((u) => u.td_group === group.code));
    } catch (e: any) {
      setMembershipError(e.message || 'Erreur lors du chargement de la composition des groupes');
    } finally {
      setMembershipLoading(false);
    }
  };

  const reloadProjectGroups = async (tdCode: string) => {
    const token = await getValidToken();
    if (!token) throw new Error('Session expirée, reconnectez-vous.');
    const allGroups = await projectGroupAdminService.list(token);
    setProjectGroups(allGroups.filter((g) => g.td_group === tdCode));
  };

  const handleAddMember = async (projectGroup: AdminProjectGroup) => {
    const userId = parseInt(addSelections[projectGroup.id] || '', 10);
    if (!userId || !managingTd) return;
    setMemberActionLoading(`add-${projectGroup.id}`);
    setMembershipError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await projectGroupAdminService.addMember(projectGroup.id, userId, token);
      setAddSelections({ ...addSelections, [projectGroup.id]: '' });
      await reloadProjectGroups(managingTd.code);
    } catch (e: any) {
      setMembershipError(e.message || "Erreur lors de l'ajout");
    } finally {
      setMemberActionLoading(null);
    }
  };

  const handleRemoveMember = async (projectGroup: AdminProjectGroup, userId: number) => {
    if (!managingTd) return;
    setMemberActionLoading(`remove-${projectGroup.id}-${userId}`);
    setMembershipError(null);
    try {
      const token = await getValidToken();
      if (!token) throw new Error('Session expirée, reconnectez-vous.');
      await projectGroupAdminService.removeMember(projectGroup.id, userId, token);
      await reloadProjectGroups(managingTd.code);
    } catch (e: any) {
      setMembershipError(e.message || 'Erreur lors du retrait');
    } finally {
      setMemberActionLoading(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>Admin Groupes</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {generateMessage && <Alert severity="success" sx={{ mb: 2 }}>{generateMessage}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Ajouter un groupe TD</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Les digits (utilisés dans les noms de dépôts GitLab) sont attribués
          automatiquement, de façon croissante et définitive.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField label="Code (ex: G8)" size="small" value={newCode} onChange={(e) => setNewCode(e.target.value)} />
          <Button variant="contained" startIcon={<PersonAddIcon />} disabled={!newCode.trim() || creating} onClick={handleCreate}>
            Ajouter
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Groupes TD ({groups.length})</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Désactiver un groupe TD non utilisé cette année (ex: pas de G5) le retire des listes de
          sélection pour les nouvelles affectations, sans toucher aux données existantes.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button variant="outlined" disabled={sandboxLoading} onClick={handleGenerateSandboxes}>
            {sandboxLoading ? <CircularProgress size={16} /> : 'Créer les bacs à sable (666 / 667)'}
          </Button>
          <Typography variant="body2" color="text.secondary">
            Deux projets de test par groupe TD actif (web-tp-projet-&lt;TD&gt;-666 et -667) pour les
            chargé·es de TP : invisibles des étudiant·es, exclus des statistiques et des notes. Créer
            ensuite leur repo et leur zip webTool depuis l'admin GitLab.
          </Typography>
        </Box>
        {loading ? <CircularProgress /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Digits</TableCell>
                  <TableCell>Actif</TableCell>
                  <TableCell align="right">Étudiants</TableCell>
                  <TableCell align="right">Groupes projet</TableCell>
                  <TableCell>Ajuster le nombre de groupes projet</TableCell>
                  <TableCell align="right">Composition</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell>{g.code}</TableCell>
                    <TableCell>{g.digits}</TableCell>
                    <TableCell>
                      <Switch checked={g.active} onChange={() => handleToggleActive(g)} />
                    </TableCell>
                    <TableCell align="right">{g.student_count}</TableCell>
                    <TableCell align="right">{g.project_group_count}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          size="small"
                          type="number"
                          placeholder="Nouveau total"
                          sx={{ width: 140 }}
                          value={generateCounts[g.id] || ''}
                          onChange={(e) => setGenerateCounts({ ...generateCounts, [g.id]: e.target.value })}
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={!generateCounts[g.id] || generateLoadingId === g.id}
                          onClick={() => handleGenerate(g)}
                        >
                          {generateLoadingId === g.id ? <CircularProgress size={16} /> : 'Générer'}
                        </Button>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Gérer la composition des binômes (arbitrage final)">
                        <IconButton size="small" onClick={() => openManage(g)}>
                          <GroupsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Composition des binômes (arbitrage final) */}
      <Dialog open={!!managingTd} onClose={() => setManagingTd(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Composition des groupes — {managingTd?.code}
          <IconButton size="small" onClick={() => setManagingTd(null)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ajouter un·e étudiant·e déjà dans un autre groupe l'en déplace directement. Retirer
            un·e étudiant·e le/la remet disponible pour l'auto-formation ou une réaffectation.
          </Typography>
          {membershipError && <Alert severity="error" sx={{ mb: 2 }}>{membershipError}</Alert>}
          {membershipLoading ? <CircularProgress /> : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Groupe</TableCell>
                    <TableCell>Membres</TableCell>
                    <TableCell>Ajouter</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectGroups.map((pg) => {
                    const availableStudents = students.filter((s) => !pg.members.some((m) => m.user_id === s.id));
                    return (
                      <TableRow key={pg.id}>
                        <TableCell>{pg.generic_name}</TableCell>
                        <TableCell>
                          {pg.members.map((m) => (
                            <Chip
                              key={m.user_id}
                              size="small"
                              label={`${m.firstname} ${m.lastname}`}
                              onDelete={() => handleRemoveMember(pg, m.user_id)}
                              disabled={memberActionLoading === `remove-${pg.id}-${m.user_id}`}
                              sx={{ mr: 0.5, mb: 0.5 }}
                            />
                          ))}
                          {pg.members.length === 0 && <Typography variant="body2" color="text.secondary">vide</Typography>}
                        </TableCell>
                        <TableCell>
                          {pg.members.length < 2 && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              <TextField
                                select
                                size="small"
                                sx={{ minWidth: 200 }}
                                value={addSelections[pg.id] || ''}
                                onChange={(e) => setAddSelections({ ...addSelections, [pg.id]: e.target.value })}
                              >
                                {availableStudents.map((s) => {
                                  const elsewhere = s.project_group && s.project_group.id !== pg.id;
                                  return (
                                    <MenuItem key={s.id} value={s.id}>
                                      {s.firstname} {s.lastname}{elsewhere ? ` (déjà dans ${s.project_group.name || s.project_group.generic_name})` : ''}
                                    </MenuItem>
                                  );
                                })}
                              </TextField>
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={!addSelections[pg.id] || memberActionLoading === `add-${pg.id}`}
                                onClick={() => handleAddMember(pg)}
                              >
                                Ajouter
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManagingTd(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminGroupsPage;
